import React, { useState } from 'react';
import moment from 'moment';
import {
  Box,
  Typography,
  TextField,
  Tooltip,
  DateTimePicker,
  Modal,
  Button,
  Checkbox,
  useTheme,
  useMediaQuery,
} from '@ublend-npm/aulaui-core';
import UpcomingAssignmentAlert from './UpcomingAssignmentAlert';
import { getDateErrorText } from '../utils/utils';

type Assignment = {
  title: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  gradesReleasedAt?: string;
  isHidden?: boolean;
  isAnonymised?: boolean;
  submissions: string[];
  extensions?: any[];
};

type TurnitinSetupFormProps = {
  assignment: Assignment;
  errors: Record<string, boolean>;
  hasAnonymizedError: boolean;
  isEditing?: boolean;
  handleChange: (
    name: string
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDateTimePicker: (name: string, date: string) => void;
  handleSwitch: (name: string) => () => void;
};

type DateFieldType = 'startDate' | 'endDate' | 'gradesReleasedAt' | null;

const DATE_FIELDS: { key: DateFieldType; label: string; testId: string }[] = [
  {
    key: 'startDate',
    label: 'Open date',
    testId: 'new-assignment-start-button',
  },
  { key: 'endDate', label: 'Due date', testId: 'new-assignment-due-button' },
  {
    key: 'gradesReleasedAt',
    label: 'Grades release date',
    testId: 'new-assignment-released-button',
  },
];

const TurnitinSetupForm = ({
  assignment,
  errors,
  hasAnonymizedError,
  isEditing,
  handleChange,
  handleDateTimePicker,
  handleSwitch,
}: TurnitinSetupFormProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [openDateField, setOpenDateField] = useState<DateFieldType>(null);
  const [pendingDate, setPendingDate] = useState<Date | null>(null);

  const {
    title,
    description,
    gradesReleasedAt,
    isHidden,
    isAnonymised,
    submissions,
    extensions,
  } = assignment;

  const hasAnonymizedOption = typeof isAnonymised === 'boolean';
  const shouldShowAnonymizedOption =
    !isEditing || (isEditing && hasAnonymizedOption);
  const hasSubmissions = submissions && submissions.length > 0;
  const isGradesReleasedPassed = gradesReleasedAt
    ? new Date(gradesReleasedAt) < new Date()
    : false;
  const hasExtensions = extensions && extensions.length > 0;
  const disabledAnonymiseSwitch =
    hasSubmissions ||
    hasAnonymizedError ||
    isGradesReleasedPassed ||
    hasExtensions;

  const anonymiseTooltip = disabledAnonymiseSwitch
    ? `The anonymise setting cannot be modified because the assignment ${
        hasExtensions
          ? 'contains extensions.'
          : 'already received submissions or grades released date is in the past.'
      }`
    : 'By default, student submissions will not be anonymised. Change this setting here.';

  const formatDate = (dateStr?: string) =>
    dateStr ? moment(dateStr).format('lll') : '';

  const getDateValue = (key: DateFieldType) => {
    if (!key) return null;
    const val = assignment[key];
    return val ? new Date(val) : null;
  };

  const switchRowSx = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: '24px',
    maxHeight: '38px',
    '&:not(:last-child)': {
      mb: '16px',
    },
  };

  const switchLabelSx = {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightRegular,
    fontStyle: 'normal',
    fontSize: '1rem',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: theme.palette.text.primary,
  };

  return (
    <Box
      data-testid="new-assignment-info"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        pt: { xs: '0.5rem', md: '1rem' },
      }}
    >
      <UpcomingAssignmentAlert />

      <TextField
        label="Title"
        aria-label="Assignment title"
        value={title}
        sx={{
          m: '0px',
          mb: errors.title ? '0.5rem' : '0px',
        }}
        onChange={handleChange('title')}
        errorText={
          errors.title
            ? 'Title is required and must be at least 2 characters'
            : undefined
        }
      />

      <TextField
        label="Description"
        maxRows={3}
        kind="multiline"
        data-testid="new-assignment-description"
        value={description ?? ''}
        onChange={handleChange('description')}
        sx={{
          m: '0px',
        }}
      />

      {/* Date fields */}
      <Box
        sx={{
          display: 'grid',
          columnGap: '1rem',
        }}
      >
        {DATE_FIELDS.map(({ key, label, testId }) => {
          const dateKey = key as NonNullable<DateFieldType>;
          return (
            <TextField
              key={dateKey}
              label={label}
              value={formatDate(assignment[dateKey])}
              data-testid={testId}
              sx={{
                cursor: 'pointer',
                m: '0px',
                mb: errors[dateKey] ? '0.5rem' : '0px',
              }}
              errorText={getDateErrorText({
                key: dateKey,
                assignment,
                errors,
              })}
              inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
              onClick={() => {
                setPendingDate(getDateValue(key) ?? new Date());
                setOpenDateField(key);
              }}
            />
          );
        })}
      </Box>

      {/* Date picker modal */}
      <Modal open={!!openDateField} onClose={() => setOpenDateField(null)}>
        <Box
          sx={{
            borderRadius: 2,
            boxShadow: 24,
            width: 320,
            maxWidth: '95vw',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ bgcolor: 'background.paper', px: 2, pt: 1.5, pb: 0 }}>
            <Typography
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '12px',
                letterSpacing: '0.08em',
              }}
            >
              Select date &amp; time
            </Typography>
          </Box>
          <DateTimePicker
            key={openDateField}
            value={pendingDate || new Date()}
            onChange={(date: any) => {
              const d = date instanceof Date ? date : date?.toDate?.() ?? null;
              if (d) setPendingDate(d);
            }}
            sx={{
              width: '100%',
              boxShadow: 'none',
              '& .MuiPickersDay-root.Mui-selected:hover': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              px: 2,
              pb: 1.5,
              pt: 0.5,
            }}
          >
            <Button kind="secondary" onClick={() => setOpenDateField(null)}>
              Cancel
            </Button>
            <Button
              kind="secondary"
              onClick={() => {
                if (openDateField) {
                  const normalized = new Date(pendingDate ?? new Date());
                  normalized.setSeconds(0, 0);
                  handleDateTimePicker(openDateField, normalized.toISOString());
                }
                setPendingDate(null);
                setOpenDateField(null);
              }}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Hidden switch */}
      <Tooltip
        title="By default, assignments are hidden from students until you are ready to make them visible."
        placement="right"
        disableHoverListener={!isDesktop}
        disableFocusListener={!isDesktop}
        disableTouchListener
      >
        <Box sx={switchRowSx}>
          <Typography sx={switchLabelSx}>Assignment is hidden</Typography>
          <Checkbox
            kind="switch"
            checked={!!isHidden}
            onChange={handleSwitch('isHidden')}
            aria-label="Assignment is hidden"
            sx={{
              mr: 0,
            }}
            label={''}
          />
        </Box>
      </Tooltip>

      {/* Anonymise switch */}
      {shouldShowAnonymizedOption && (
        <Tooltip
          title={anonymiseTooltip}
          placement="right"
          disableHoverListener={!isDesktop}
          disableFocusListener={!isDesktop}
          disableTouchListener
        >
          <Box sx={switchRowSx}>
            <Typography sx={switchLabelSx}>
              Anonymise student submissions
            </Typography>
            <Checkbox
              kind="switch"
              disabled={disabledAnonymiseSwitch}
              checked={!!isAnonymised}
              onChange={handleSwitch('isAnonymised')}
              aria-label="Anonymise student submissions"
              label={''}
              sx={{
                mr: 0,
              }}
            />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default TurnitinSetupForm;
